import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { api, type AutoRechargeConfig } from "@/lib/api";
import { formatMoney } from "@/lib/format";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

// Auto-recharge settings dialog, opened from the "Auto-recharge on/off" link
// on the credit-balance card. Enabling requires a saved card — the backend
// enforces it too.
export function AutoRechargeDialog({
  open,
  onOpenChange,
  config,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  config: AutoRechargeConfig;
}) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(config.enabled);
  const [threshold, setThreshold] = useState(config.thresholdUsd?.toString() ?? "10");
  const [amount, setAmount] = useState(config.amountUsd?.toString() ?? "25");
  const [cap, setCap] = useState(config.monthlyCapUsd?.toString() ?? "");

  // Re-sync when a background refetch delivers new server truth (e.g.
  // returning from the save-card redirect).
  const [prevConfig, setPrevConfig] = useState(config);
  if (prevConfig !== config) {
    setPrevConfig(config);
    setEnabled(config.enabled);
    setThreshold(config.thresholdUsd?.toString() ?? "10");
    setAmount(config.amountUsd?.toString() ?? "25");
    setCap(config.monthlyCapUsd?.toString() ?? "");
  }

  const save = useMutation({
    mutationFn: () =>
      api.billing.updateAutoRecharge({
        enabled,
        thresholdUsd: Number(threshold),
        amountUsd: Number(amount),
        monthlyCapUsd: cap.trim() === "" ? null : Number(cap),
      }),
    onSuccess: () => {
      toast.success("Auto-recharge settings saved");
      // Write the saved values straight into the cache so the balance-card
      // link flips immediately, then refetch for server truth.
      queryClient.setQueryData<AutoRechargeConfig>(["billing-auto-recharge"], (old) =>
        old
          ? {
              ...old,
              enabled,
              thresholdUsd: Number(threshold),
              amountUsd: Number(amount),
              monthlyCapUsd: cap.trim() === "" ? null : Number(cap),
            }
          : old,
      );
      queryClient.invalidateQueries({ queryKey: ["billing-auto-recharge"] });
      onOpenChange(false);
    },
  });

  const thresholdN = Number(threshold);
  const amountN = Number(amount);
  const capN = cap.trim() === "" ? null : Number(cap);
  const valid =
    Number.isFinite(thresholdN) &&
    thresholdN >= 1 &&
    thresholdN <= 1000 &&
    Number.isFinite(amountN) &&
    amountN >= 10 &&
    amountN <= 25_000 &&
    (capN === null || (Number.isFinite(capN) && capN >= amountN));

  return (
    <Dialog open={open} onOpenChange={(o) => !save.isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Auto-recharge</DialogTitle>
          <DialogDescription>
            When your balance drops below the amount you set, we charge your saved
            card and add credits.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {config.disabledReason === "payment_failures" && (
            <div className="flex items-center gap-2 rounded-md border border-destructive/50 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              <AlertTriangle className="size-4 shrink-0" />
              Auto-recharge was turned off after repeated payment failures. Update
              your payment method, then save to re-enable.
            </div>
          )}

          <div className="flex items-center justify-between gap-4">
            <div className="text-sm font-medium">Automatically add credits</div>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={!config.paymentMethod && !enabled}
            />
          </div>
          {!config.paymentMethod && (
            <p className="text-xs text-muted-foreground">
              Save a payment method first to turn on auto-recharge.
            </p>
          )}

          {enabled && (
            <>
          <div className="space-y-2">
            <Label htmlFor="ar-threshold" className="text-xs">
              When balance falls below (USD)
            </Label>
            <Input
              id="ar-threshold"
              type="number"
              min={1}
              max={1000}
              step="1"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ar-amount" className="text-xs">
              Add this amount (USD)
            </Label>
            <Input
              id="ar-amount"
              type="number"
              min={10}
              max={25000}
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ar-cap" className="text-xs">
              Most to charge per month (optional)
            </Label>
            <Input
              id="ar-cap"
              type="number"
              min={10}
              step="1"
              placeholder="No limit"
              value={cap}
              onChange={(e) => setCap(e.target.value)}
            />
            {config.enabled && config.monthlyCapUsd !== null && (
              <p className="text-xs text-muted-foreground">
                {formatMoney(config.monthUsedUsd)} of {formatMoney(config.monthlyCapUsd)} used
                this month.
              </p>
            )}
          </div>
            </>
          )}

          {save.isError && <InlineError error={save.error ?? "Save failed"} />}
        </div>

        <DialogFooter>
          <Button onClick={() => valid && save.mutate()} disabled={!valid || save.isPending}>
            {save.isPending ? "Saving…" : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
